import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export interface VideoProps {
  src: string;
  /** Ordered native sources; when provided, the video has no src attribute. */
  sources?: { src: string; type: string }[];
  className?: string;
  mode?: 'autoplay' | 'hover';
  contained?: boolean;
  loop?: boolean;
  inViewThreshold?: number | number[];
  /** Gate autoplay on intersection and document visibility, without an autoPlay attribute. */
  strictViewportPlayback?: boolean;
  /** Initial playback position; subsequent native loops play the entire video. */
  startTime?: number;
  poster?: string;
  preload?: 'none' | 'metadata' | 'auto';
  onFirstFrame?: () => void;
  onLoadedData?: () => void;
  onError?: (details?: { code?: number; currentSrc: string }) => void;
  onEnded?: () => void;
  children?: ReactNode;
  style?: CSSProperties;
  crossOrigin?: 'anonymous' | 'use-credentials';
}

export default function Video({
  src,
  sources,
  className,
  mode = 'autoplay',
  onLoadedData,
  onError,
  onEnded,
  children,
  contained = false,
  loop = true,
  inViewThreshold = 0.5,
  strictViewportPlayback = false,
  startTime = 0,
  poster,
  preload,
  onFirstFrame,
  style,
  crossOrigin,
}: VideoProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hovering = useRef(false);
  const seekStage = useRef<'metadata' | 'seeking' | 'decoding' | 'ready'>(
    startTime > 0 ? 'metadata' : 'ready',
  );
  const [playbackReady, setPlaybackReady] = useState(startTime <= 0);
  const sourceKey = sources?.map(({ src, type }) => `${src}:${type}`).join('|');
  const [documentVisible, setDocumentVisible] = useState(
    () => typeof document === 'undefined' || !document.hidden,
  );
  const { ref: inViewRef, inView } = useInView({
    threshold:
      typeof inViewThreshold === 'number' ? [inViewThreshold] : inViewThreshold,
    skip: mode !== 'autoplay',
  });

  useEffect(() => {
    if (startTime <= 0) {
      seekStage.current = 'ready';
      setPlaybackReady(true);
      return;
    }
    const video = videoRef.current;
    video?.pause();
    seekStage.current = 'metadata';
    setPlaybackReady(false);
  }, [src, sourceKey, startTime]);

  const frameReady = () => {
    if (seekStage.current !== 'decoding') return;
    seekStage.current = 'ready';
    setPlaybackReady(true);
    onLoadedData?.();
  };

  const fallBackToStart = (video: HTMLVideoElement) => {
    seekStage.current = 'decoding';
    try {
      video.currentTime = 0;
    } catch {
      // Some streams do not support seeking; wait for their first frame.
    }
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) frameReady();
  };

  useEffect(() => {
    if (!strictViewportPlayback) return;
    const updateVisibility = () => setDocumentVisible(!document.hidden);
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    return () =>
      document.removeEventListener('visibilitychange', updateVisibility);
  }, [strictViewportPlayback]);

  useEffect(() => {
    if (mode === 'hover') {
      if (startTime > 0 && playbackReady && hovering.current) {
        void videoRef.current?.play()?.catch(() => {
          // A hover-triggered play may be rejected by the browser.
        });
      }
      return;
    }
    if (mode !== 'autoplay') return;
    if (
      playbackReady &&
      inView &&
      (!strictViewportPlayback || documentVisible)
    ) {
      void videoRef.current?.play()?.catch(() => {
        // Autoplay can be rejected by the browser; leave the poster visible.
      });
    } else {
      videoRef.current?.pause();
    }
  }, [
    inView,
    mode,
    strictViewportPlayback,
    documentVisible,
    src,
    playbackReady,
    startTime,
  ]);

  return (
    <div ref={inViewRef} className={className} style={style}>
      <video
        ref={videoRef}
        onMouseEnter={() => {
          hovering.current = true;
          if (mode === 'hover') {
            if (startTime <= 0 || seekStage.current === 'ready')
              void videoRef.current?.play();
          }
        }}
        onMouseLeave={() => {
          hovering.current = false;
          if (mode === 'hover') {
            videoRef.current?.pause();
          }
        }}
        src={sources?.length ? undefined : src}
        poster={poster}
        preload={preload}
        crossOrigin={crossOrigin}
        className={`h-full w-full rounded-[inherit] object-${
          contained ? 'contain' : 'cover'
        }${startTime > 0 && !playbackReady ? ' opacity-0' : ''}`}
        muted
        playsInline
        loop={loop}
        disablePictureInPicture
        autoPlay={
          mode === 'autoplay' && !strictViewportPlayback && startTime <= 0
        }
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={(event) => {
          if (seekStage.current !== 'metadata') return;
          const video = event.currentTarget;
          if (Number.isFinite(video.duration) && video.duration <= startTime) {
            fallBackToStart(video);
            return;
          }
          try {
            seekStage.current = 'seeking';
            video.currentTime = startTime;
          } catch {
            fallBackToStart(video);
          }
        }}
        onSeeked={(event) => {
          if (seekStage.current !== 'seeking') return;
          const video = event.currentTarget;
          if (
            !Number.isFinite(video.currentTime) ||
            Math.abs(video.currentTime - startTime) > 0.5
          ) {
            fallBackToStart(video);
            return;
          }
          seekStage.current = 'decoding';
          if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA)
            frameReady();
        }}
        onCanPlay={frameReady}
        onLoadedData={() => {
          if (startTime <= 0) onLoadedData?.();
          else frameReady();
        }}
        onPlaying={(event) => {
          if (seekStage.current !== 'ready') return;
          if (!onFirstFrame) return;
          const video = event.currentTarget;
          if (video.requestVideoFrameCallback) {
            video.requestVideoFrameCallback(() => {
              if (
                video.isConnected &&
                !video.paused &&
                seekStage.current === 'ready'
              )
                onFirstFrame();
            });
          } else {
            onFirstFrame();
          }
        }}
        onError={(event) => {
          // A rejected <source> can still be followed by a playable codec.
          if (event.target !== event.currentTarget) return;
          onError?.({
            code: event.currentTarget.error?.code,
            currentSrc: event.currentTarget.currentSrc,
          });
        }}
        onEnded={() => onEnded?.()}
      >
        {sources?.map((source) => (
          <source key={source.src} src={source.src} type={source.type} />
        ))}
        {children}
      </video>
    </div>
  );
}
